/**
 *
 * SignUpStepTwo
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import _isNil from 'lodash/isNil';
import { makeSelectStoredItem } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import DemographicsSelect from 'components/DemographicsSelect';
import { shouldDisplayQuestion } from 'components/DemographicsSelect/utils';
import MonthPicker from 'components/MonthPicker';
import ProfileTextField from 'components/ProfileTextField';
import SubscriptionOptions from 'components/SubscriptionOptions';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import useSiteCopySelector from 'components/useSiteCopySelector';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: 8,
    marginBottom: 8,
  },
  email: {
    marginTop: 10,
    fontSize: 14,
    marginBottom: 4,
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    // marginTop: 16,
    marginBottom: 5,
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
}));

const SignUpStepTwo = ({ email, onSubmit }) => {
  const classes = useStyles();
  const clientDetails = useSelector(makeSelectClientDetails());
  const [subscriptionOption] = useSiteCopySelector(['account']);
  const [siteConfig] = useSiteConfigSelector(['Age and Gender Mappings']);
  const storedItem = useSelector(makeSelectStoredItem());

  const isMonthYearFromAssessment =
    _get(storedItem, 'type') === 'assessmentResult' &&
    !_isNil(_get(storedItem, 'payload.birthdayMonthYear'));

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('-1');
  const [genderOther, setGenderOther] = useState('');
  const [genderLGBTQp, setGenderLGBTQp] = useState('-1');
  const [stateOfResidence, setStateOfResidence] = useState('-1');
  const [monthYear, setMonthYear] = useState(
    isMonthYearFromAssessment
      ? _get(storedItem, 'payload.birthdayMonthYear')
      : null,
  );
  const [subscription, setSubscription] = useState(
    _get(clientDetails, 'metadata.hideSubscription')
      ? []
      : _get(subscriptionOption, 'pageCopy.subscription', [])
          .filter(item => item.default)
          .map(item => item.value),
  );
  const [error, setError] = useState([]);

  const userProfileFields = _get(clientDetails, 'metadata.userProfileFields');
  const signUpUserProfileFields = _get(
    clientDetails,
    'metadata.signUpUserProfileFields',
  );

  const shouldIncludeFieldInProfile = fieldName =>
    signUpUserProfileFields
      ? signUpUserProfileFields.includes(fieldName)
      : userProfileFields
      ? userProfileFields.includes(fieldName)
      : true;

  const handleFormChange = e => {
    if (e.target.name === 'firstName') {
      setFirstName(e.target.value);
      if (error.includes('firstName')) {
        setError(error.filter(item => item !== 'firstName'));
      } else if (e.target.value === '') {
        setError(error.concat('firstName'));
      }
    } else if (e.target.name === 'lastName') {
      setLastName(e.target.value);
      if (error.includes('lastName')) {
        setError(error.filter(item => item !== 'lastName'));
      } else if (e.target.value === '') {
        setError(error.concat('lastName'));
      }
    } else if (e.target.name === 'gender') {
      if (e.target.value !== 'Other') {
        setGenderOther('');
      }
      setGender(e.target.value);
    } else if (e.target.name === 'genderOther') setGenderOther(e.target.value);
    else if (e.target.name === 'genderLGBTQp') setGenderLGBTQp(e.target.value);
    else if (e.target.name === 'stateOfResidence') {
      setStateOfResidence(e.target.value);
      if (error.includes('stateOfResidence')) {
        setError(error.filter(item => item !== 'stateOfResidence'));
      }
    }
  };

  const handleMonthYearChange = value => setMonthYear(value);

  const handleSubscriptionChange = value => {
    if (subscription.includes(value)) {
      setSubscription(subscription.filter(item => item !== value));
    } else {
      setSubscription(subscription.concat(value));
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    const formErrors = [];

    if (shouldIncludeFieldInProfile('firstName') && firstName === '') {
      formErrors.push('firstName');
    }
    if (shouldIncludeFieldInProfile('lastName') && lastName === '') {
      formErrors.push('lastName');
    }
    if (
      shouldDisplayQuestion(
        siteConfig,
        'stateOfResidence',
        clientDetails.shortName,
      ) &&
      stateOfResidence === '-1'
    ) {
      formErrors.push('stateOfResidence');
    }

    if (formErrors.length === 0) {
      onSubmit({
        firstName,
        lastName,
        monthYear,
        gender,
        genderOther,
        genderLGBTQp,
        stateOfResidence,
        subscription,
      });
    } else {
      setError(formErrors);
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="caption" align="center" className={classes.email}>
          {email}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <form className={classes.container} onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            {shouldIncludeFieldInProfile('firstName') && (
              <Grid item xs={6}>
                <ProfileTextField
                  field="firstName"
                  value={firstName}
                  onChange={handleFormChange}
                  plain
                  required
                  error={error.includes('firstName')}
                />
              </Grid>
            )}
            {shouldIncludeFieldInProfile('lastName') && (
              <Grid item xs={6}>
                <ProfileTextField
                  field="lastName"
                  value={lastName}
                  onChange={handleFormChange}
                  plain
                  required
                  error={error.includes('lastName')}
                />
              </Grid>
            )}
            {shouldIncludeFieldInProfile('birthdayMonthYear') && (
              <Grid item xs={6}>
                <MonthPicker
                  value={monthYear}
                  onChange={handleMonthYearChange}
                  disabled={isMonthYearFromAssessment}
                  disableFutureDates
                  plain
                  modal
                  preventDisabledDates
                />
              </Grid>
            )}
            {shouldIncludeFieldInProfile('gender') && (
              <Grid item xs={6}>
                <DemographicsSelect
                  type="gender"
                  onChange={handleFormChange}
                  value={gender}
                  plain
                />
              </Grid>
            )}
            {gender === 'Other' && (
              <Grid item xs={12} sm={6}>
                <ProfileTextField
                  field="genderOther"
                  value={genderOther}
                  onChange={handleFormChange}
                  plain
                />
              </Grid>
            )}
            {shouldIncludeFieldInProfile('genderLGBTQp') &&
              shouldDisplayQuestion(
                siteConfig,
                'genderLGBTQp',
                clientDetails.shortName,
              ) && (
                <Grid item xs={12} sm={6}>
                  <DemographicsSelect
                    type="genderLGBTQp"
                    onChange={handleFormChange}
                    value={genderLGBTQp}
                    plain
                  />
                </Grid>
              )}
            {shouldIncludeFieldInProfile('stateOfResidence') &&
              shouldDisplayQuestion(
                siteConfig,
                'stateOfResidence',
                clientDetails.shortName,
              ) && (
                <Grid item xs={12} sm={6}>
                  <DemographicsSelect
                    type="stateOfResidence"
                    onChange={handleFormChange}
                    value={stateOfResidence}
                    plain
                    error={error.includes('stateOfResidence')}
                    required
                  />
                </Grid>
              )}
            {!_get(clientDetails, 'metadata.hideSubscription') && (
              <Grid item xs={12}>
                <SubscriptionOptions
                  subscription={subscription}
                  onChange={handleSubscriptionChange}
                  isSmall
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <ButtonBase className={classes.button} type="submit">
                SIGN UP
              </ButtonBase>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </>
  );
};

SignUpStepTwo.propTypes = {
  email: PropTypes.string,
  onSubmit: PropTypes.func,
};

export default SignUpStepTwo;
